<template>
  <template v-if="unwrap">
    <div ref="rootEl" class="lazy-load-sentinel" aria-hidden="true" />
    <slot v-if="intersected" />
    <slot v-if="!intersected" name="placeholder" />
  </template>
  <div v-else ref="rootEl">
    <slot v-if="intersected" />
    <slot v-if="!intersected" name="placeholder" />
  </div>
</template>

<script lang="ts" setup>
interface Props {
  /**
   * Renders slot content as siblings instead of wrapping it in a container div.
   * Use when the parent layout relies on direct children (e.g. content-section gaps).
   */
  unwrap?: boolean;
  /**
   * IntersectionObserver rootMargin — loads content before it enters the viewport.
   * @example '200px 0px 200px 0px'
   */
  rootMargin?: string;
  /**
   * Skip intersection waiting and render slot content immediately (client-only).
   */
  eager?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  unwrap: false,
  rootMargin: '0px',
  eager: false
});

const nuxtApp = useNuxtApp();
const rootEl = ref<Element>();
const observer = ref<IntersectionObserver | null>(null);
/**
 * SSR renders slot content for SEO and hydration parity. During the initial
 * client hydration pass we keep the same markup; lazy intersection only applies
 * after hydration or on purely client-side navigations.
 */
const intersected = ref(
  props.eager || import.meta.server || nuxtApp.isHydrating
);

function mountIntersectionObserver() {
  if (!rootEl.value || props.eager) {
    return;
  }

  if ('IntersectionObserver' in window) {
    observer.value?.disconnect();
    observer.value = new IntersectionObserver(
      ([firstEntry]) => {
        intersected.value = firstEntry?.isIntersecting ?? false;

        if (intersected.value) {
          observer.value?.disconnect();
        }
      },
      { rootMargin: props.rootMargin }
    );

    observer.value.observe(rootEl.value);
    return;
  }

  intersected.value = true;
}

watch(
  () => props.eager,
  (eager) => {
    if (eager) {
      intersected.value = true;
      observer.value?.disconnect();
      observer.value = null;
    }
  }
);

onMounted(() => {
  if (props.eager || intersected.value) {
    intersected.value = true;
    return;
  }

  mountIntersectionObserver();
});

onUnmounted(() => {
  observer.value?.disconnect();
  observer.value = null;
});
</script>

<style scoped>
.lazy-load-sentinel {
  height: 1px;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  pointer-events: none;
}
</style>
